import { Unit as BaseUnit } from 'army-builder-shared';

import { FWWDataLibrary } from '../fww-data-library';
import { getUnitCost } from '../functions/getUnitCost';
import { getArmour } from '../functions/unit-functions';
import { getStat } from '../functions/unit-functions';

import { UnitAI } from './ai.model';
import { Armour } from './armour.model';
import { Equipment } from './equipment.model';
import { Item } from './item.model';
import { Modifier } from './modifier.model';
import { Perk } from './perk.model';
import { SkillBlock } from './skill-block.model';
import { StatBlock } from './stat-block.model';
import { UnitTemplate } from './unit-template.model';

export interface UnitOption {
    key: string;
    equipped?: boolean;
    overrideCost?: number;
    pickedUpDuringBattle?: boolean;
    critPoints?: number;
    primed?: boolean;
    reloaded?: boolean;
    damaged?: boolean;
}

export interface DerivedUnitStats {
    cost: number;
    armour?: Armour;
    perks?: Perk[];
    gifts?: Perk[];
    scars?: Perk[];
    equipment: Equipment[];
    defaultEquipment: Equipment[];
    leaderCard?: Item;
    status?: any;
    heroic?: boolean;
    modifiers: {
        addiction?: boolean;
        name: string;
        type: string;
        effects: Modifier[];
    }[];
    skills: SkillBlock;
    stats: StatBlock;
    ai: UnitAI;
    referenceCards?: any[];
}

export interface Unit extends BaseUnit {
    userImage?: string;
    unitTemplate: UnitTemplate;
    valid: boolean;
    inRoster?: boolean;
    selectedAI?: string;
    overrideCost?: number;

    // Stuff that can be added
    options: {
        leaderCard: UnitOption;
        equipment: UnitOption[];
        perks: UnitOption[];
        skills: UnitOption[];
        gifts: UnitOption[];
        scars: UnitOption[];
        automatronComponents: UnitOption[];
    };

    derived?: DerivedUnitStats;
    isRPG?: any;
}

export type UnitCardType = 'base' | 'includeItemModifiers';

const emptyUnitTemplateCache = {};
export const getEmptyUnit = (unitTemplate: UnitTemplate, mode: UnitCardType = 'includeItemModifiers'): Unit => {
    const newId = Math.ceil(Math.random() * 100000).toString();
    if (emptyUnitTemplateCache[unitTemplate._id] && unitTemplate.key !== 'ROBOTS-AUTOMATRON') {
        // Automatron is excluded from caching as it is dynamically generated based on selected components, and caching could lead to stale data issues.
        return {
            ...structuredClone(emptyUnitTemplateCache[unitTemplate._id]),
            id: newId
        };
    }
    const unit = {
        id: newId,
        selectedOptions: [],
        variantId: null,
        valid: true,
        validationErrors: [],
        platoonId: 0,
        unitTemplate,
        unitTemplateId: unitTemplate._id,
        inRoster: true,
        options: {
            equipment: [...unitTemplate.defaultEquipment.map((de) => ({ key: de, equipped: true, overrideCost: 0 }))],
            perks: [...unitTemplate.defaultPerks.map((p) => ({ key: p, equipped: true, overrideCost: 0 }))],
            skills: [],
            gifts: [],
            scars: [],
            leaderCard: null,
            automatronComponents: []
        }
    };

    const derived = getDerivedStats(unit);

    const res = { ...unit, derived };

    emptyUnitTemplateCache[unitTemplate._id] = res;
    return structuredClone(res);
};

export const getDerivedStats = (unit: Unit, includeItemModifers = true): DerivedUnitStats => {
    const findMatchingItems = (unitOptions: UnitOption[], allItems: { [key: string]: Item }) =>
        unitOptions
            .map((o) => {
                const item = structuredClone(allItems[o.key]);
                if (!item) {
                    const msg = 'Unrecognized item key: ' + o.key;
                    console.error(msg);
                    throw msg;
                }
                return {
                    ...item,
                    equipped: o.equipped,
                    overrideCost: o.overrideCost
                };
            })
            .filter((x) => x);

    const findMatchingEquipment = (unitOptions: UnitOption[]) =>
        unitOptions
            .map((o) => {
                const item = FWWDataLibrary.equipment[o.key];
                if (Object.keys(FWWDataLibrary.equipment).length === 0) {
                    throw 'FWWDataLibrary.equipment not yet loaded';
                }
                if (!item) {
                    const msg = 'Unrecognized equipment key: ' + o.key;
                    console.error(msg);
                    return null;
                }

                const res = {
                    ...item,
                    equipped: o.equipped,
                    damaged: o.damaged,
                    overrideCost: o.overrideCost,
                    primed: o.primed ?? item.primed,
                    critPoints: o.critPoints ?? item.critPoints,
                    reloaded: (o.reloaded ?? (item.reloaded !== undefined && item.reloaded !== null)) ? true : null
                };

                if (res.damaged) {
                    res.mod = [...item.mod];
                    res.mod.reverse();
                }

                return res;
            })
            .filter((x) => x);

    const equipment: any[] = findMatchingEquipment(unit.options.equipment);
    const defaultEquipment: any[] = findMatchingEquipment(unit.unitTemplate.defaultEquipment.map((key) => ({ key })));
    const perks: any[] = findMatchingItems(unit.options.perks, FWWDataLibrary.perks);

    let leaderCard = null;
    if (unit.options.leaderCard) {
        const lc = FWWDataLibrary.leaders[unit.options.leaderCard.key];
        if (lc) {
            leaderCard = {
                ...lc,
                overrideCost: unit.options.leaderCard.overrideCost
            };
        }
    }

    let ai = unit.unitTemplate.ai?.[0];

    if (unit.selectedAI) {
        ai = unit.unitTemplate.ai.find((x) => x.name === unit.selectedAI);
    }

    const derived: DerivedUnitStats = {
        cost: 0,
        armour: null,
        perks,
        gifts: unit.derived?.gifts || [],
        scars: unit.derived?.scars || [],
        stats: unit.derived?.stats || {},
        skills: unit.derived?.skills || {},
        equipment,
        defaultEquipment,
        leaderCard,
        status: unit.derived?.status || {},
        modifiers: unit.derived?.modifiers || [],
        heroic: !!unit.options.perks.find((p) => p.key === 'HEROIC'),
        ai,
        referenceCards: unit.unitTemplate.referenceCards?.map((rc) => FWWDataLibrary.referenceCards[rc])
    };

    const u = {
        ...unit,
        derived
    };

    const mode: UnitCardType = includeItemModifers ? 'includeItemModifiers' : 'base';
    const armour: Armour = {
        physical: getArmour(u, 'physical', mode),
        physical_hard: getArmour(u, 'physical_hard', mode),
        energy: getArmour(u, 'energy', mode),
        energy_hard: getArmour(u, 'energy_hard', mode),
        radiation: getArmour(u, 'radiation', mode),
        radiation_hard: getArmour(u, 'radiation_hard', mode)
    };

    derived.armour = armour;
    derived.cost = getUnitCost(u);
    derived.status = {
        ...derived.status,
        addicted: !!derived.modifiers.find((m) => m.addiction)
    };
    derived.stats = {
        S: getStat(u, 'S', mode),
        P: getStat(u, 'P', mode),
        E: getStat(u, 'E', mode),
        C: getStat(u, 'C', mode),
        I: getStat(u, 'I', mode),
        A: getStat(u, 'A', mode),
        L: getStat(u, 'L', mode)
    };
    u.derived = derived;
    // derived.skills = {
    //     // ...Object.keys(u.unitTemplate.skills).reduce((agg, key) => ({ ...agg, [key]: getSkill(u, key, 'includeItemModifiers') }), {})
    // };

    return derived;
};
